<script setup lang="ts">
import {useId} from "vue";

defineProps<{
  moments: VideoMoment[];
  title?: string;
  lazy?: boolean;
}>()

const selectedIndex = ref<number|null>(null);
const carousel = ref<HTMLElement | null>(null);
const headingId = useId()

const scrollLeft = () => {
  carousel.value?.scrollBy({left: -300, behavior: 'smooth'});
};

const scrollRight = () => {
  carousel.value?.scrollBy({left: 300, behavior: 'smooth'});
};

const handleMomentOpened = (id: number) => {
  selectedIndex.value = selectedIndex.value === id ? null : id;
};
</script>

<template>
  <section class="wad4-moment-carousel" :aria-labelledby="headingId">
    <div class="wad4-moment-carousel__header">
      <h2 v-if="title" class="wad4-moment-carousel__header-title" :id="headingId">
        <slot name="title">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="currentColor">
            <path d="M19 9.28906L20.25 6.53906L23 5.28906L20.25 4.03906L19 1.28906L17.75 4.03906L15 5.28906L17.75 6.53906L19 9.28906Z" fill="currentColor"/>
            <path d="M19 15.2891L17.75 18.0391L15 19.2891L17.75 20.5391L19 23.2891L20.25 20.5391L23 19.2891L20.25 18.0391L19 15.2891Z" fill="currentColor"/>
            <path d="M11.5 9.78906L9 4.28906L6.5 9.78906L1 12.2891L6.5 14.7891L9 20.2891L11.5 14.7891L17 12.2891L11.5 9.78906ZM9.99 13.2791L9 15.4591L8.01 13.2791L5.83 12.2891L8.01 11.2991L9 9.11906L9.99 11.2991L12.17 12.2891L9.99 13.2791Z" fill="currentColor"/>
          </svg>
          {{ title }}
        </slot>
      </h2>
      <div class="wad4-moment-carousel__header-controls">
        <ButtonIcon @pointerup="scrollLeft">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.77 3.77L16 2L6 12L16 22L17.77 20.23L9.54 12L17.77 3.77Z" fill="currentColor"/>
          </svg>
        </ButtonIcon>
        <ButtonIcon @pointerup="scrollRight">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
            <path d="M6.23047 20.8296L8.00047 22.5996L18.0005 12.5996L8.00047 2.59961L6.23047 4.36961L14.4605 12.5996L6.23047 20.8296Z" fill="currentColor"/>
          </svg>
        </ButtonIcon>
      </div>
    </div>
    <div ref="carousel" class="wad4-moment-carousel__track">
      <VideoMoment class="wad4-moment-carousel__card"
        v-for="(moment, idx) in moments"
        @momentOpened="handleMomentOpened(idx)"
        :isOpen="selectedIndex === idx"
        :key="`moment-${idx}`"
        :moment="moment"
        :lazy="lazy"/>
    </div>
  </section>
</template>

<style lang="scss">
.wad4-moment-carousel {
  display: flex;
  height: auto;
  flex-direction: column;
  align-items: flex-start;
  gap: $wad4-spacing-3xl;
  align-self: stretch;


  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;

    &-title {
      font-family: $wad4-default-font-sans;
      @include text-size-mixin('lg');
      font-weight: $wad4-font-weight-semi-bold;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: $wad4-spacing-md;
      color: $wad4-c-text-text-primary;
      margin: unset;
    }

    &-controls {
      display: flex;
      align-items: center;
      gap: $wad4-spacing-xs;
      flex-wrap: nowrap;
    }
  }

  &__track {
    display: flex;
    align-items: flex-start;
    gap: $wad4-container-padding;
    flex: none;
    align-self: stretch;
    overflow-x: auto;
    overflow-y: hidden;
    height: auto;

    scroll-snap-type: x mandatory;
    scroll-padding-left: $wad4-container-padding;

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__card {
    --wad4-moment-carousel-cards: 1;
    --wad4-moment-carousel-gaps: 1;
    --wad4-moment-carousel-card-max-width: 290px;
    --wad4-moment-carousel-card-partial: 0.10;

    scroll-snap-align: center;
    flex: 0 0 min(calc((100% - ($wad4-container-padding * var(--wad4-moment-carousel-gaps))) / (var(--wad4-moment-carousel-cards) + var(--wad4-moment-carousel-card-partial))), var(--wad4-moment-carousel-card-max-width));

    @include mqBreakPoint(480px) {
      --wad4-moment-carousel-cards: 2;
      --wad4-moment-carousel-gaps: 3;
      --wad4-moment-carousel-card-partial: 0.25;
    }

    @include mqBreakPoint(768px) {
      --wad4-moment-carousel-cards: 3;
      --wad4-moment-carousel-gaps: 4;
      --wad4-moment-carousel-card-max-width: 380px;
    }

    @include mqBreakPoint(1200px) {
      --wad4-moment-carousel-cards: 5;
      --wad4-moment-carousel-gaps: 6;
      --wad4-moment-carousel-card-max-width: 533px;
    }

  }

}
</style>
